import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { ocrApi } from './ocr.api';
import { ocrKeys } from './ocr.queries';
import type {
  DocumentChunk,
  ExtractedDataItem,
  OcrResult,
  PageTemplateAssignment,
  CreateFieldHighlightRequest,
  CreateOcrFieldRequest,
  RenameOcrFieldRequest,
  DeleteFieldHighlightRequest,
  UpdateChunkBboxRequest,
  UpdateFieldRequest
} from '../types';

/**
 * Optimistically patch the *container* results cache — the data the OCR
 * results page (and the QA detail page) actually render via
 * `useContainerResultsLive`. The per-field mutations historically only patched
 * `ocrKeys.result(resultId)`, which those pages never read, so an accepted /
 * edited field stayed visually unverified until the best-effort WebSocket
 * broadcast happened to arrive — or the user reloaded. Patching the container
 * cache here makes the accepting user's own action reflect instantly.
 *
 * Returns the previous cache value so the caller can roll back on error.
 */
function patchContainerResult(
  queryClient: QueryClient,
  containerId: number,
  resultId: number,
  patch: (result: OcrResult) => OcrResult
): { data: OcrResult[] } | undefined {
  const key = ocrKeys.containerResults(containerId);
  const prev = queryClient.getQueryData<{ data: OcrResult[] }>(key);
  if (prev) {
    queryClient.setQueryData<{ data: OcrResult[] }>(key, {
      ...prev,
      data: prev.data.map((r) => (r.id === resultId ? patch(r) : r))
    });
  }
  return prev;
}

export function useStartOcrMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      containerId,
      ocrTemplateIds,
      pageTemplateAssignments
    }: {
      containerId: number;
      ocrTemplateIds?: number[];
      /** "Assign per page" flow — takes precedence over ocrTemplateIds. */
      pageTemplateAssignments?: PageTemplateAssignment[];
    }) =>
      ocrApi.start(containerId, { ocrTemplateIds, pageTemplateAssignments }),
    // Toast is owned by the StartOcrButton via `useOcrToast()` so it can
    // surface a deep-link action — skip the global success message here.
    meta: {
      operationType: 'create',
      skipGlobalSuccessToast: true
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ocrKeys.containerResults(variables.containerId)
      });
      queryClient.invalidateQueries({ queryKey: ocrKeys.containers() });
      queryClient.invalidateQueries({ queryKey: ocrKeys.activeJobs() });
    }
  });
}

/**
 * Create an ad-hoc OCR test container from uploaded images/PDF. It lands in the
 * OCR list; the operator starts OCR from there via the normal flow. The global
 * success toast surfaces the backend message.
 */
export function useCreateTestContainerMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      formData,
      onProgress
    }: {
      formData: FormData;
      /** Byte-level upload progress for this chunk (0-100). */
      onProgress?: (progress: number) => void;
    }) => ocrApi.createTestContainer(formData, onProgress),
    meta: { operationType: 'create' },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ocrKeys.containers() });
    }
  });
}

/**
 * Append a chunk of page images to a test container. Used by the chunked
 * upload loop for large documents; the per-chunk success toast is suppressed
 * (the dialog shows aggregate progress and a single final toast).
 */
export function useAppendTestContainerPagesMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      containerId,
      formData,
      onProgress
    }: {
      containerId: number;
      formData: FormData;
      /** Byte-level upload progress for this chunk (0-100). */
      onProgress?: (progress: number) => void;
    }) => ocrApi.appendTestContainerPages(containerId, formData, onProgress),
    meta: { operationType: 'create', skipGlobalSuccessToast: true },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ocrKeys.containers() });
    }
  });
}

/** Delete a test container (cleanup). */
export function useDeleteTestContainerMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('ocr');

  return useMutation({
    mutationFn: (containerId: number) =>
      ocrApi.deleteTestContainer(containerId),
    meta: {
      operationType: 'delete',
      fallbackSuccessMessage: t('testContainerDeleted')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ocrKeys.containers() });
    }
  });
}

export function useRefuseOcrContainerMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('repairs');

  return useMutation({
    mutationFn: ({
      containerId,
      ...data
    }: {
      containerId: number;
      repair_destination_id?: number;
      repair_reason_id: number;
      notes?: string;
    }) => ocrApi.refuse(containerId, data),
    meta: {
      operationType: 'create',
      fallbackSuccessMessage: t('transferSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ocrKeys.containers() });
    }
  });
}

/**
 * Pause every in-flight run on a container. The success message comes from the
 * backend (it spells out that already-extracted pages are kept), and both the
 * listing and the active-jobs widget need to re-read: a paused run leaves the
 * "in progress" bucket.
 */
export function usePauseOcrContainerMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ containerId }: { containerId: number }) =>
      ocrApi.pause(containerId),
    meta: { operationType: 'update' },
    onSuccess: (_data, { containerId }) => {
      queryClient.invalidateQueries({ queryKey: ocrKeys.containers() });
      queryClient.invalidateQueries({ queryKey: ocrKeys.activeJobs() });
      queryClient.invalidateQueries({
        queryKey: ocrKeys.containerResults(containerId)
      });
    }
  });
}

/** Resume a paused container, re-dispatching only the pages that never ran. */
export function useResumeOcrContainerMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ containerId }: { containerId: number }) =>
      ocrApi.resume(containerId),
    meta: { operationType: 'update' },
    onSuccess: (_data, { containerId }) => {
      queryClient.invalidateQueries({ queryKey: ocrKeys.containers() });
      queryClient.invalidateQueries({ queryKey: ocrKeys.activeJobs() });
      queryClient.invalidateQueries({
        queryKey: ocrKeys.containerResults(containerId)
      });
    }
  });
}

/**
 * Force-finish a run stuck on "processing" because a page-batch worker died.
 *
 * Recovery also happens by itself, in band — this is for the operator looking at
 * a container that has not moved and wanting it resolved now. The same three
 * caches as pause/resume need re-reading: the run leaves the in-progress bucket
 * and picks up its failed page ranges.
 */
export function useRecoverOcrContainerMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ containerId }: { containerId: number }) =>
      ocrApi.recover(containerId),
    meta: { operationType: 'update' },
    onSuccess: (_data, { containerId }) => {
      queryClient.invalidateQueries({ queryKey: ocrKeys.containers() });
      queryClient.invalidateQueries({ queryKey: ocrKeys.activeJobs() });
      queryClient.invalidateQueries({
        queryKey: ocrKeys.containerResults(containerId)
      });
    }
  });
}

/**
 * Re-extract a single page of a QA-rejected run. Invalidates the container's
 * results so the page picks up its in-flight state (`rerunning_page_numbers`)
 * without disturbing the other pages' rendered data.
 */
export function useRerunOcrPageMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      resultId,
      pageNumber
    }: {
      resultId: number;
      pageNumber: number;
      containerId?: number;
    }) => ocrApi.rerunPage(resultId, pageNumber),
    meta: { operationType: 'update' },
    onSuccess: (_data, { resultId, containerId }) => {
      queryClient.invalidateQueries({ queryKey: ocrKeys.result(resultId) });
      if (containerId != null) {
        queryClient.invalidateQueries({
          queryKey: ocrKeys.containerResults(containerId)
        });
      }
      queryClient.invalidateQueries({ queryKey: ocrKeys.activeJobs() });
    }
  });
}

/**
 * Re-run the selected pages of an existing run with their chosen templates
 * (OMA-674). The caller raises its own toast with a deep link, like start.
 */
export function useRerunOcrPagesMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      containerId,
      pages
    }: {
      containerId: number;
      pages: PageTemplateAssignment[];
    }) => ocrApi.rerunPages(containerId, pages),
    meta: { operationType: 'update', skipGlobalSuccessToast: true },
    onSuccess: (_data, { containerId }) => {
      // `containerResults` is the root the summary header is nested under, so
      // this refreshes the results and the header together. The dialog's
      // page list (`containerPages`) is a separate root and needs nothing:
      // a re-run changes no page.
      queryClient.invalidateQueries({
        queryKey: ocrKeys.containerResults(containerId)
      });
      queryClient.invalidateQueries({ queryKey: ocrKeys.containers() });
      queryClient.invalidateQueries({ queryKey: ocrKeys.activeJobs() });
    }
  });
}

/**
 * Retry one page's ranking after a failure: drops the page's cached entry and
 * queues a fresh single-page job, then lets the page query poll it.
 */
export function useRetryPageSuggestionMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      containerId,
      pageId
    }: {
      containerId: number;
      pageId: number;
    }) =>
      ocrApi.getPageTemplateSuggestion(containerId, pageId, { refresh: true }),
    meta: { operationType: 'create', skipGlobalSuccessToast: true },
    onSuccess: (data, { containerId, pageId }) => {
      queryClient.setQueryData(
        ocrKeys.pageTemplateSuggestion(containerId, pageId),
        data
      );
    }
  });
}

export function useRegeneratePageSuggestionsMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (containerId: number) =>
      ocrApi.regeneratePageTemplateSuggestions(containerId),
    meta: { operationType: 'create', skipGlobalSuccessToast: true },
    onSuccess: (_data, containerId) => {
      queryClient.invalidateQueries({
        queryKey: ocrKeys.pageTemplateSuggestions(containerId)
      });
    }
  });
}

export function useRegeneratePoolSuggestionsMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (registrationId: number) =>
      ocrApi.regeneratePoolSuggestions(registrationId),
    meta: { operationType: 'create', skipGlobalSuccessToast: true },
    onSuccess: (_data, registrationId) => {
      queryClient.invalidateQueries({
        queryKey: ocrKeys.templateSuggestions(registrationId)
      });
    }
  });
}

export function useSyncRegistrationOcrTemplatesMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('ocr');

  return useMutation({
    mutationFn: ({
      registrationId,
      templateIds
    }: {
      registrationId: number;
      templateIds: number[];
    }) => ocrApi.syncRegistrationTemplates(registrationId, templateIds),
    meta: {
      operationType: 'update',
      fallbackSuccessMessage: t('templatePoolUpdated')
    },
    onSuccess: (_data, { registrationId }) => {
      queryClient.invalidateQueries({
        queryKey: ocrKeys.registrationTemplates(registrationId)
      });
    }
  });
}

export function useUpdateOcrFieldMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('ocr');

  return useMutation({
    mutationFn: ({
      resultId,
      key,
      data
    }: {
      resultId: number;
      key: string;
      data: UpdateFieldRequest;
      /** Patches the rendered container-results cache for instant feedback. */
      containerId?: number;
    }) => ocrApi.updateField(resultId, key, data),
    meta: {
      operationType: 'update',
      fallbackSuccessMessage: t('fieldUpdated')
    },
    onMutate: async ({ resultId, key, data, containerId }) => {
      await queryClient.cancelQueries({
        queryKey: ocrKeys.result(resultId)
      });
      const prev = queryClient.getQueryData<{ data: OcrResult }>(
        ocrKeys.result(resultId)
      );
      const patchField = (f: ExtractedDataItem) =>
        f.key === key &&
        (data.page_number == null || f.page_number === data.page_number)
          ? { ...f, value: data.value, status: 'processed' as const }
          : f;
      if (prev) {
        queryClient.setQueryData<{ data: OcrResult }>(
          ocrKeys.result(resultId),
          {
            ...prev,
            data: {
              ...prev.data,
              extracted_data: prev.data.extracted_data?.map(patchField) ?? null
            }
          }
        );
      }
      let prevContainer: { data: OcrResult[] } | undefined;
      if (containerId != null) {
        await queryClient.cancelQueries({
          queryKey: ocrKeys.containerResults(containerId)
        });
        prevContainer = patchContainerResult(
          queryClient,
          containerId,
          resultId,
          (r) => ({
            ...r,
            extracted_data: r.extracted_data?.map(patchField) ?? null
          })
        );
      }
      return { prev, prevContainer };
    },
    onError: (_err, { resultId, containerId }, context) => {
      if (context?.prev) {
        queryClient.setQueryData(ocrKeys.result(resultId), context.prev);
      }
      if (context?.prevContainer && containerId != null) {
        queryClient.setQueryData(
          ocrKeys.containerResults(containerId),
          context.prevContainer
        );
      }
    },
    onSettled: (_data, _err, { resultId }) => {
      queryClient.invalidateQueries({
        queryKey: ocrKeys.result(resultId)
      });
    }
  });
}

/**
 * Manual drag-resize/move of a chunk's bounding box. Structural clone of
 * `useUpdateOcrFieldMutation`'s optimistic-update + rollback + settle
 * pattern, but patches `chunks[].bbox` instead of `extracted_data[].value`.
 * Success toast is intentionally suppressed — dragging a box is a frequent,
 * self-evidently-successful visual action; a toast on every drag would be
 * noisy. Errors still surface via the global MutationCache.
 */
export function useUpdateChunkBboxMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      resultId,
      chunkId,
      data
    }: {
      resultId: number;
      chunkId: number;
      data: UpdateChunkBboxRequest;
      /** Patches the rendered container-results cache for instant feedback. */
      containerId?: number;
    }) => ocrApi.updateChunkBbox(resultId, chunkId, data),
    meta: {
      operationType: 'update',
      skipGlobalSuccessToast: true
    },
    onMutate: async ({ resultId, chunkId, data, containerId }) => {
      await queryClient.cancelQueries({ queryKey: ocrKeys.result(resultId) });
      const prev = queryClient.getQueryData<{ data: OcrResult }>(
        ocrKeys.result(resultId)
      );
      const patchChunk = (c: DocumentChunk) =>
        c.id === chunkId ? { ...c, bbox: data } : c;
      if (prev) {
        queryClient.setQueryData<{ data: OcrResult }>(
          ocrKeys.result(resultId),
          {
            ...prev,
            data: {
              ...prev.data,
              chunks: prev.data.chunks?.map(patchChunk) ?? prev.data.chunks
            }
          }
        );
      }
      let prevContainer: { data: OcrResult[] } | undefined;
      if (containerId != null) {
        await queryClient.cancelQueries({
          queryKey: ocrKeys.containerResults(containerId)
        });
        prevContainer = patchContainerResult(
          queryClient,
          containerId,
          resultId,
          (r) => ({
            ...r,
            chunks: r.chunks?.map(patchChunk) ?? r.chunks
          })
        );
      }
      return { prev, prevContainer };
    },
    onError: (_err, { resultId, containerId }, context) => {
      if (context?.prev) {
        queryClient.setQueryData(ocrKeys.result(resultId), context.prev);
      }
      if (context?.prevContainer && containerId != null) {
        queryClient.setQueryData(
          ocrKeys.containerResults(containerId),
          context.prevContainer
        );
      }
    },
    onSettled: (_data, _err, { resultId }) => {
      queryClient.invalidateQueries({ queryKey: ocrKeys.result(resultId) });
    }
  });
}

/**
 * Create the first highlight for a field that has none (OMA-592).
 *
 * Not optimistic: the chunk id is minted server-side, so there is nothing to
 * patch in until the response lands. Both cache roots that render the field are
 * invalidated — the container-results root is what the results page actually
 * reads (see features/CLAUDE.md on covering every root).
 */
export function useCreateFieldHighlightMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      resultId,
      data
    }: {
      resultId: number;
      data: CreateFieldHighlightRequest;
      /** Only used to invalidate the container-results cache. */
      containerId?: number;
    }) => ocrApi.createFieldHighlight(resultId, data),
    meta: { operationType: 'create' },
    onSuccess: (_res, { resultId, containerId }) => {
      queryClient.invalidateQueries({ queryKey: ocrKeys.result(resultId) });
      if (containerId != null) {
        queryClient.invalidateQueries({
          queryKey: ocrKeys.containerResults(containerId)
        });
      }
    }
  });
}

/**
 * Add a field the extraction never produced, with its value and its first
 * highlight (OMA-630).
 *
 * Not optimistic, for the same reason as the highlight mutation above and one
 * more: the field's KEY is minted server-side (folded from the typed name, then
 * collision-suffixed), so there is no id to patch in optimistically and no way
 * to guess which key the field will end up under.
 */
export function useCreateOcrFieldMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      resultId,
      data
    }: {
      resultId: number;
      data: CreateOcrFieldRequest;
      /** Only used to invalidate the container-results cache. */
      containerId?: number;
    }) => ocrApi.createField(resultId, data),
    meta: { operationType: 'create' },
    onSuccess: (_res, { resultId, containerId }) => {
      queryClient.invalidateQueries({ queryKey: ocrKeys.result(resultId) });
      if (containerId != null) {
        queryClient.invalidateQueries({
          queryKey: ocrKeys.containerResults(containerId)
        });
      }
    }
  });
}

/**
 * Rename a field (OMA-631).
 *
 * Invalidates the same two roots as every other field write: the panel renders
 * off the container-results root, and the label the card shows lives on the
 * result row that root serves. Not optimistic — the server refuses a name that
 * folds to nothing or collides with another field on the page, and patching the
 * card first would show the operator a name the result never accepted.
 */
export function useRenameOcrFieldMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      resultId,
      data
    }: {
      resultId: number;
      data: RenameOcrFieldRequest;
      /** Only used to invalidate the container-results cache. */
      containerId?: number;
    }) => ocrApi.renameField(resultId, data),
    meta: { operationType: 'update' },
    onSuccess: (_res, { resultId, containerId }) => {
      queryClient.invalidateQueries({ queryKey: ocrKeys.result(resultId) });
      if (containerId != null) {
        queryClient.invalidateQueries({
          queryKey: ocrKeys.containerResults(containerId)
        });
      }
    }
  });
}

/**
 * Unlink one highlight from a field (OMA-592). Same invalidation reasoning as
 * the create mutation above — the results page reads the container root.
 */
export function useDeleteFieldHighlightMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      resultId,
      chunkId,
      data
    }: {
      resultId: number;
      /** `document_chunks.id` — what the route binds. */
      chunkId: number;
      data: DeleteFieldHighlightRequest;
      /** Only used to invalidate the container-results cache. */
      containerId?: number;
    }) => ocrApi.deleteFieldHighlight(resultId, chunkId, data),
    meta: { operationType: 'delete' },
    onSuccess: (_res, { resultId, containerId }) => {
      queryClient.invalidateQueries({ queryKey: ocrKeys.result(resultId) });
      if (containerId != null) {
        queryClient.invalidateQueries({
          queryKey: ocrKeys.containerResults(containerId)
        });
      }
    }
  });
}

export function useAcceptOcrFieldMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('ocr');

  return useMutation({
    mutationFn: ({
      resultId,
      key,
      pageNumber
    }: {
      resultId: number;
      key: string;
      /** Page-scopes the accept for repeating templates (same key per page). */
      pageNumber?: number | null;
      /** Patches the rendered container-results cache for instant feedback. */
      containerId?: number;
    }) => ocrApi.acceptField(resultId, key, pageNumber),
    meta: {
      operationType: 'update',
      fallbackSuccessMessage: t('fieldAccepted')
    },
    onMutate: async ({ resultId, key, pageNumber, containerId }) => {
      await queryClient.cancelQueries({
        queryKey: ocrKeys.result(resultId)
      });
      const prev = queryClient.getQueryData<{ data: OcrResult }>(
        ocrKeys.result(resultId)
      );
      const patchField = (f: ExtractedDataItem) =>
        f.key === key && (pageNumber == null || f.page_number === pageNumber)
          ? { ...f, status: 'processed' as const }
          : f;
      if (prev) {
        queryClient.setQueryData<{ data: OcrResult }>(
          ocrKeys.result(resultId),
          {
            ...prev,
            data: {
              ...prev.data,
              extracted_data: prev.data.extracted_data?.map(patchField) ?? null
            }
          }
        );
      }
      let prevContainer: { data: OcrResult[] } | undefined;
      if (containerId != null) {
        await queryClient.cancelQueries({
          queryKey: ocrKeys.containerResults(containerId)
        });
        prevContainer = patchContainerResult(
          queryClient,
          containerId,
          resultId,
          (r) => ({
            ...r,
            extracted_data: r.extracted_data?.map(patchField) ?? null
          })
        );
      }
      return { prev, prevContainer };
    },
    onError: (_err, { resultId, containerId }, context) => {
      if (context?.prev) {
        queryClient.setQueryData(ocrKeys.result(resultId), context.prev);
      }
      if (context?.prevContainer && containerId != null) {
        queryClient.setQueryData(
          ocrKeys.containerResults(containerId),
          context.prevContainer
        );
      }
    },
    onSettled: (_data, _err, { resultId }) => {
      queryClient.invalidateQueries({
        queryKey: ocrKeys.result(resultId)
      });
    }
  });
}

export function useRetryOcrMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('ocr');

  return useMutation({
    mutationFn: (resultId: number) => ocrApi.retry(resultId),
    meta: {
      operationType: 'update',
      fallbackSuccessMessage: t('retrySuccess')
    },
    onSuccess: (_data, resultId) => {
      queryClient.invalidateQueries({ queryKey: ocrKeys.result(resultId) });
      queryClient.invalidateQueries({ queryKey: ocrKeys.containers() });
      queryClient.invalidateQueries({ queryKey: ocrKeys.activeJobs() });
    }
  });
}

export function useRetryFailedOcrBatchesMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('ocr');

  return useMutation({
    mutationFn: (resultId: number) => ocrApi.retryFailedBatches(resultId),
    meta: {
      operationType: 'update',
      fallbackSuccessMessage: t('retrySuccess')
    },
    onSuccess: (_data, resultId) => {
      queryClient.invalidateQueries({ queryKey: ocrKeys.result(resultId) });
      queryClient.invalidateQueries({ queryKey: ocrKeys.containers() });
      queryClient.invalidateQueries({ queryKey: ocrKeys.activeJobs() });
    }
  });
}

/**
 * Bulk-verify every field the panel attributes to one page, in a single
 * request. No optimistic cache patch: the page section already shows its own
 * in-flight state (`markAccepting`), and mirroring the backend's three-signal
 * page attribution client-side just to pre-paint statuses is how the old
 * per-field loop drifted from the server in the first place. The settled
 * invalidations pull the authoritative statuses.
 */
export function useAcceptAllOcrFieldsOnPageMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('ocr');

  return useMutation({
    mutationFn: ({
      resultId,
      pageNumber
    }: {
      resultId: number;
      pageNumber: number;
      containerId?: number;
    }) => ocrApi.acceptAllOnPage(resultId, pageNumber),
    meta: {
      operationType: 'update',
      fallbackSuccessMessage: t('allAccepted')
    },
    onSettled: (_data, _err, { resultId, containerId }) => {
      queryClient.invalidateQueries({ queryKey: ocrKeys.result(resultId) });
      queryClient.invalidateQueries({ queryKey: ocrKeys.containers() });
      if (containerId != null) {
        queryClient.invalidateQueries({
          queryKey: ocrKeys.containerResults(containerId)
        });
      }
    }
  });
}

export function useAcceptAllOcrFieldsMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('ocr');

  return useMutation({
    mutationFn: ({ resultId }: { resultId: number; containerId?: number }) =>
      ocrApi.acceptAll(resultId),
    meta: {
      operationType: 'update',
      fallbackSuccessMessage: t('allAccepted')
    },
    onMutate: async ({ resultId, containerId }) => {
      await queryClient.cancelQueries({
        queryKey: ocrKeys.result(resultId)
      });
      const prev = queryClient.getQueryData<{ data: OcrResult }>(
        ocrKeys.result(resultId)
      );
      const markAllProcessed = (f: ExtractedDataItem) => ({
        ...f,
        status: 'processed' as const
      });
      if (prev) {
        queryClient.setQueryData<{ data: OcrResult }>(
          ocrKeys.result(resultId),
          {
            ...prev,
            data: {
              ...prev.data,
              status: 'completed',
              extracted_data:
                prev.data.extracted_data?.map(markAllProcessed) ?? null
            }
          }
        );
      }
      let prevContainer: { data: OcrResult[] } | undefined;
      if (containerId != null) {
        await queryClient.cancelQueries({
          queryKey: ocrKeys.containerResults(containerId)
        });
        prevContainer = patchContainerResult(
          queryClient,
          containerId,
          resultId,
          (r) => ({
            ...r,
            status: 'completed',
            extracted_data: r.extracted_data?.map(markAllProcessed) ?? null
          })
        );
      }
      return { prev, prevContainer };
    },
    onError: (_err, { resultId, containerId }, context) => {
      if (context?.prev) {
        queryClient.setQueryData(ocrKeys.result(resultId), context.prev);
      }
      if (context?.prevContainer && containerId != null) {
        queryClient.setQueryData(
          ocrKeys.containerResults(containerId),
          context.prevContainer
        );
      }
    },
    onSettled: (_data, _err, { resultId, containerId }) => {
      queryClient.invalidateQueries({
        queryKey: ocrKeys.result(resultId)
      });
      queryClient.invalidateQueries({ queryKey: ocrKeys.containers() });
      // Reconcile the rendered cache so the freshly-verified field statuses
      // surface without a reload. acceptAll only verifies a result now — it no
      // longer submits to QA (see useSubmitContainerToQaMutation).
      if (containerId != null) {
        queryClient.invalidateQueries({
          queryKey: ocrKeys.containerResults(containerId)
        });
      }
    }
  });
}

/**
 * Submit a whole container to OCR QA (tier 1). This is the ONLY path that sets
 * qa_stage=pending_qa1 and advances the registration ocr → qa1_ocr; the backend
 * rejects with 422 unless every field of every page is accepted. Decoupled from
 * acceptAll so verifying a single page can never push the container into QA.
 */
export function useSubmitContainerToQaMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ containerId }: { containerId: number }) =>
      ocrApi.submitToQa(containerId),
    // The caller (handleSubmitAll) owns the success toast + redirect.
    meta: { operationType: 'update', skipGlobalSuccessToast: true },
    onSuccess: (_data, { containerId }) => {
      queryClient.invalidateQueries({
        queryKey: ocrKeys.containerResults(containerId)
      });
      queryClient.invalidateQueries({ queryKey: ocrKeys.containers() });
      queryClient.invalidateQueries({ queryKey: ocrKeys.activeJobs() });
    }
  });
}
